import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { fetchProgram } from "@/lib/api";
import { waLink, WA_MESSAGES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/whatsapp";
import { SITE_URL } from "@/lib/site";
import SEO from "@/components/SEO";

export default function ProgramDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    fetchProgram(slug)
      .then((data) => setP(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!p) return <div className="section-pad container-x">Loading…</div>;

  const msg = WA_MESSAGES[p.cta_message_key] || WA_MESSAGES.program(p.title);

  const courseJsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: p.title,
    description: p.description,
    provider: {
      "@type": "Organization",
      name: "Weight Loss Gurukul",
      sameAs: `${SITE_URL}/`,
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      courseWorkload: p.duration,
      inLanguage: "en",
    },
  };

  return (
    <div data-testid="program-detail" className="section-pad">
      <SEO
        title={`${p.title} — ${p.benefit || p.tagline}`}
        description={`${p.description} ${p.duration} · ${p.level}. Book a free consultation on WhatsApp today.`}
        path={`/programs/${p.slug}`}
        type="article"
        keywords={`${p.title}, ${p.benefit || ""}, online yoga program, live yoga classes, weight loss online, PCOS yoga, detox yoga`}
        jsonLd={courseJsonLd}
      />
      <div className="container-x">
        <Link to="/programs" data-testid="back-to-programs" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> All programs
        </Link>

        <div className="mt-6 grid lg:grid-cols-5 gap-10 md:gap-12 items-start">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-clay text-white">{p.duration}</span>
              <span className="text-ink-muted">· {p.level}</span>
            </div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl text-forest leading-tight">{p.title}</h1>
            <p className="mt-3 md:mt-4 text-xl md:text-2xl text-clay italic font-serif">{p.tagline}</p>
            <p className="mt-3 text-base md:text-lg text-ink-soft">{p.benefit}</p>
            <p className="mt-5 md:mt-6 text-ink-soft leading-relaxed">{p.description}</p>

            <h3 className="mt-10 font-serif text-2xl text-forest">What you get</h3>
            <ul className="mt-4 space-y-3">
              {p.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-ink-soft">
                  <CheckCircle2 className="h-5 w-5 text-forest shrink-0 mt-0.5" />
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-2 lg:sticky lg:top-24">
            <div className="rounded-3xl border border-sand-300 bg-white p-7 md:p-8 shadow-sm">
              {p.badge && (
                <span className="inline-block mb-4 rounded-full bg-clay text-white px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-medium">
                  {p.badge}
                </span>
              )}
              <div className="eyebrow">Investment</div>
              <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                {p.price_strike && (
                  <span className="text-sm line-through text-ink-muted">{p.price_strike}</span>
                )}
                <span className="font-serif text-4xl md:text-5xl text-forest leading-none">{p.price_display || "—"}</span>
              </div>
              <p className="mt-5 text-sm text-ink-soft">Talk to Dhruva on WhatsApp. We'll understand your goals and recommend the right path — no commitment.</p>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={waLink(msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white shadow-sm hover:shadow-md transition-all"
                >
                  <MessageCircle className="h-4 w-4" /> Book Your Free Consultation
                </a>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="detail-call-cta"
                  className="btn-outline w-full"
                >
                  <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                </a>
              </div>

              <ul className="mt-6 space-y-2 text-xs text-ink-soft">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-forest" /> Free 1:1 consultation</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-forest" /> 7-day money-back guarantee</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
